import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTypewriter } from "../../hooks/useTypewriter";
import { useCarouselScroll } from "../../hooks/useCarouselScroll";
import { useChapterIntersection } from "../../hooks/useChapterIntersection";
import { usePageState } from "../../hooks/usePageState";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { chapters, CONTACT_HEADLINE, CONTACT_SUBHEAD } from "./data";
import { DotNavigation } from "./DotNavigation";
import { AboutChapter } from "./AboutChapter";
import { TRANSITION_DELAY } from "../../../utils/animationConstants";
import "./styles/base.css";
import "./styles/dot-navigation.css";
import "./styles/chapter.css";
import "./styles/contact.css";
import "./styles/typewriter.css";
import "./styles/responsive.css";

const contactChapter = chapters[chapters.length - 1];

type AboutProps = { mode: "about" | "contact" };

export const About = ({ mode }: AboutProps) => {
  const isContactMode = mode === "contact";
  const firstChapterId = chapters[0]?.id ?? "about";
  const containerRef = useRef<HTMLElement | null>(null);
  const pageState = usePageState();
  const carouselEnabled = useMediaQuery("(max-width: 1023px)");
  const [activeChapterId, setActiveChapterId] = useState(
    isContactMode ? "contact" : firstChapterId,
  );
  const [revealedChapterIds, setRevealedChapterIds] = useState<string[]>(
    isContactMode ? ["contact"] : [firstChapterId],
  );
  const [hasScrolled, setHasScrolled] = useState(false);
  const [contactExpanded, setContactExpanded] = useState(isContactMode);
  const [typewriterActive, setTypewriterActive] = useState(false);
  const [linksVisible, setLinksVisible] = useState(false);
  const [typewriterDoneIds, setTypewriterDoneIds] = useState<string[]>([]);
  const activeChapterRef = useRef(activeChapterId);
  const typewriterDoneRef = useRef<
    Record<string, { headline: boolean; subhead: boolean }>
  >({});

  const contactHeadlineFrom = isContactMode ? "" : contactChapter.headline;
  const contactSubheadFrom = isContactMode ? "" : contactChapter.subhead;

  const headline = useTypewriter({
    from: contactHeadlineFrom,
    to: CONTACT_HEADLINE,
    active: typewriterActive,
    delay: 0,
  });

  const subhead = useTypewriter({
    from: contactSubheadFrom,
    to: CONTACT_SUBHEAD,
    active: typewriterActive,
    delay: 0,
  });

  const typingDone = headline.isDone && subhead.isDone;
  const showCursor = headline.isAnimating || subhead.isAnimating;
  const linksReady = typingDone;

  useEffect(() => {
    if (!linksReady) return;
    // Delay by one frame so the element is in the DOM at opacity 0
    // before the is-visible class triggers the CSS fade-in.
    const id = requestAnimationFrame(() => setLinksVisible(true));
    return () => cancelAnimationFrame(id);
  }, [linksReady]);

  useEffect(() => {
    // In contact mode, activate typewriter when page is ready
    if (!isContactMode) {
      setTypewriterActive(false);
      return;
    }

    // For contact mode, activate typewriter based on page state
    if (pageState === "subpage") {
      // Small delay to ensure component is fully mounted
      const timer = setTimeout(
        () => setTypewriterActive(true),
        TRANSITION_DELAY.MEDIUM,
      );
      return () => clearTimeout(timer);
    }
  }, [isContactMode, pageState]);

  const handleExpandContact = useCallback(() => {
    setContactExpanded(true);
    setTypewriterActive(false);
    requestAnimationFrame(() => setTypewriterActive(true));

    // On mobile, scroll the contact chapter to the top so the form
    // slides into view instead of the button disappearing off-screen.
    requestAnimationFrame(() => {
      const container = containerRef.current;
      if (!container) return;
      const contactEl = container.querySelector<HTMLElement>("#contact");
      if (contactEl) {
        contactEl.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  }, []);

  useEffect(() => {
    activeChapterRef.current = activeChapterId;
  }, [activeChapterId]);

  const revealedSet = useMemo(
    () => new Set(revealedChapterIds),
    [revealedChapterIds],
  );

  const typewriterDoneSet = useMemo(
    () => new Set(typewriterDoneIds),
    [typewriterDoneIds],
  );

  const markTypewriterDone = useCallback(
    (chapterId: string, part: "headline" | "subhead") => {
      const current = typewriterDoneRef.current[chapterId] ?? {
        headline: false,
        subhead: false,
      };
      if (current[part]) return;
      const next = { ...current, [part]: true };
      typewriterDoneRef.current[chapterId] = next;
      if (next.headline && next.subhead) {
        setTypewriterDoneIds((prev) =>
          prev.includes(chapterId) ? prev : [...prev, chapterId],
        );
      }
    },
    [],
  );

  const handleRevealChapter = useCallback((id: string) => {
    setRevealedChapterIds((current) => {
      if (current.includes(id)) return current;
      return [...current, id];
    });
  }, []);

  const handleActivateChapter = useCallback(
    (id: string) => {
      if (id !== activeChapterRef.current) {
        setActiveChapterId(id);
      }
    },
    [],
  );

  useCarouselScroll({
    containerRef,
    enabled: carouselEnabled,
    activeChapterId,
    contactExpanded,
    isContactMode,
  });

  useChapterIntersection({
    containerRef,
    enabled: !isContactMode,
    onRevealChapter: handleRevealChapter,
    onActivateChapter: handleActivateChapter,
  });

  useEffect(() => {
    if (isContactMode) return;
    const container = containerRef.current;
    if (!container) return;

    const onScroll = () => {
      if (hasScrolled || container.scrollTop <= 6) return;
      setHasScrolled(true);
    };

    container.addEventListener("scroll", onScroll, { passive: true });
    return () => container.removeEventListener("scroll", onScroll);
  }, [hasScrolled, isContactMode]);

  const goToChapter = (chapterId: string) => {
    const container = containerRef.current;
    if (!container) return;

    const chapter = container.querySelector<HTMLElement>(`#${chapterId}`);
    if (!chapter) return;
    chapter.scrollIntoView({ behavior: "smooth", block: "start" });
    setHasScrolled(true);
  };

  const chaptersToRender = isContactMode ? [contactChapter] : chapters;

  const reducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const getContactHeadline = () => {
    if (isContactMode) return reducedMotion ? CONTACT_HEADLINE : headline.text;
    if (!contactExpanded) return contactChapter.headline;
    if (reducedMotion) return CONTACT_HEADLINE;
    return headline.text;
  };

  const getContactSubhead = () => {
    if (isContactMode) return reducedMotion ? CONTACT_SUBHEAD : subhead.text;
    if (!contactExpanded) return contactChapter.subhead;
    if (reducedMotion) return CONTACT_SUBHEAD;
    return subhead.text;
  };

  return (
    <section
      ref={containerRef}
      className={`about-panel${isContactMode ? " about-panel--contact-mode" : ""}${carouselEnabled && !isContactMode ? " about-panel--carousel" : ""}${contactExpanded ? " about-panel--contact-expanded" : ""}`}
      aria-label="About chapters"
    >
      {!isContactMode && (
        <DotNavigation
          chapters={chapters}
          activeChapterId={activeChapterId}
          onNavigate={goToChapter}
        />
      )}
      {chaptersToRender.map((chapter, index) => {
        const isActive = isContactMode || chapter.id === activeChapterId;
        const isVisible = isContactMode || revealedSet.has(chapter.id);
        const isPageReady = pageState === "subpage";
        const isTypewriterDone = reducedMotion
          ? true
          : chapter.id === "contact"
            ? isContactMode
              ? typingDone
              : contactExpanded
                ? typingDone
                : typewriterDoneSet.has(chapter.id)
            : typewriterDoneSet.has(chapter.id);

        return (
          <AboutChapter
            key={chapter.id}
            chapter={chapter}
            index={index}
            isActive={isActive}
            isVisible={isVisible}
            isPageReady={isPageReady}
            isContactMode={isContactMode}
            isLastChapter={index === chaptersToRender.length - 1}
            contactExpanded={contactExpanded}
            typewriterDone={isTypewriterDone}
            reducedMotion={reducedMotion}
            hasScrolled={hasScrolled}
            showCursor={showCursor}
            headlineText={getContactHeadline()}
            subheadText={getContactSubhead()}
            headlineAnimating={headline.isAnimating}
            subheadAnimating={subhead.isAnimating}
            linksReady={linksReady}
            linksVisible={linksVisible}
            typingDone={typingDone}
            onExpandContact={handleExpandContact}
            onTypewriterDone={(part) => markTypewriterDone(chapter.id, part)}
          />
        );
      })}
    </section>
  );
};
